import { useEffect, useRef, useState } from 'react';
import SolidCheckbox from './SolidCheckbox';
import Input from "../reuseable/Input"

const CheckboxTree = ({ data, get_value, init_data } = {}) => {
  const [search_value, set_search_value] = useState("");

  const [checkedItems, setCheckedItems] = useState(new Set(init_data || []));
  const [expandedItems, setExpandedItems] = useState(new Set(['all']));

  // =========================
  // Helpers
  // =========================
  const getAllChildIds = (node) => {
    const ids = [];
    if (node.children) {
      node.children.forEach(child => {
        ids.push(child.id);
        ids.push(...getAllChildIds(child));
      });
    }
    return ids;
  };

  const areAllChildrenChecked = (node) => {
    if (!node.children?.length) return false;
    const childIds = getAllChildIds(node);
    return childIds.every(id => checkedItems.has(id));
  };

  const areSomeChildrenChecked = (node) => {
    if (!node.children?.length) return false;
    const childIds = getAllChildIds(node);
    return childIds.some(id => checkedItems.has(id));
  };

  // =========================
  // Checkbox Change
  // =========================
  const handleCheckboxChange = (node) => {
    setCheckedItems(prev => {
      const next = new Set(prev);

      if (node.children) {
        const ids = getAllChildIds(node);
        const allChecked = ids.every(id => next.has(id));

        ids.forEach(id => {
          allChecked ? next.delete(id) : next.add(id);
        });
      } else {
        next.has(node.id) ? next.delete(node.id) : next.add(node.id);
      }

      return next;
    });
  };

  // =========================
  // Expand Toggle
  // =========================
  const toggleExpand = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // =========================
  // Tree Filter (PURE FUNCTION)
  // =========================
  const filterTreeByLabel = () => {

    if (!search_value || search_value.trim() === '') {
      return {
        filtered: data,
        expandIds: new Set(),
      };
    }

    const searchLower = search_value.toLowerCase();
    const expandIds = new Set();

    function filterNode(node) {
      const isMatch = node.label.toLowerCase().includes(searchLower);

      // Process children first
      let filteredChildren = [];

      if (node.children && node.children.length > 0) {
        filteredChildren = node.children
          .map(child => filterNode(child))
          .filter(Boolean);
      }

      // If current node matches → expand it
      if (isMatch) {
        if (node.children?.length > 0) {
          expandIds.add(node.id);
        }

        // Return full node if match
        return {
          ...node,
          children: node.children
        };
      }

      // If any child matches → expand parent + return filtered children
      if (filteredChildren.length > 0) {
        expandIds.add(node.id);

        return {
          ...node,
          children: filteredChildren
        };
      }

      return null;
    }

    return {
      filtered: data
        .map(node => filterNode(node))
        .filter(Boolean),

      expandIds
    };
  };


  // =========================
  // Tree Data Memo
  // =========================
  const treeData = useRef([
    {
      id: 'all',
      label: 'Semua',
      children: data,
    },
  ])

  // =========================
  // filter data
  // =========================

  const on_filter = () => {
    const { expandIds, filtered } = filterTreeByLabel();

    treeData.current = [
      {
        id: 'all',
        label: 'Semua',
        children: [...filtered],
      },
    ];

    if (expandIds.size > 0) {
      setExpandedItems(prev => {
        const next = new Set(prev);
        expandIds.forEach(id => next.add(id));
        next.add('all');
        return next;
      });
    }
  }

  // =========================
  // Emit Checked Value
  // =========================

  useEffect(() => {
    get_value?.([...checkedItems]);
  }, [checkedItems, get_value]);


  // =========================
  // Render Tree Node
  // =========================
  const TreeNode = ({ node, level = 0, isLast = true, parentChain = [] }) => {
    const hasChildren = node.children?.length > 0;
    const isExpanded = expandedItems.has(node.id);
    const isChecked = hasChildren
      ? areAllChildrenChecked(node)
      : checkedItems.has(node.id);

    const isIndeterminate = !isChecked && areSomeChildrenChecked(node);

    return (
      <div style={{ position: 'relative' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 0',
          position: 'relative',
          paddingLeft: level ? `${level * 40}px` : 0
        }}>

          {parentChain.map((showLine, i) => (
            <div key={i} style={{
              position: 'absolute',
              left: '-32px',
              top: 0,
              bottom: 0,
              width: '32px',
              pointerEvents: 'none',
              marginLeft: `${(i + 1) * 40}px`
            }}>
              {showLine && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: '-6px',
                  bottom: 0,
                  width: '1px',
                  background: '#d1d5db'
                }} />
              )}
            </div>
          ))}

          {level > 0 && (
            <div style={{
              position: 'absolute',
              left: '-32px',
              top: 0,
              bottom: 0,
              width: '32px',
              pointerEvents: 'none',
              marginLeft: `${level * 40}px`
            }}>
              <div style={{
                position: 'absolute',
                left: 0,
                top: '-6px',
                bottom: isLast ? '50%' : 0,
                width: '1px',
                background: '#d1d5db'
              }} />
              <div style={{
                position: 'absolute',
                left: 0,
                top: '50%',
                width: '24px',
                height: '1px',
                background: '#d1d5db'
              }} />
            </div>
          )}

          {hasChildren ? (
            <button
              onClick={() => toggleExpand(node.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12"
                style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: '0.2s' }}>
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </button>
          ) : (
            <div style={{ width: '20px', height: '20px', flexShrink: 0 }} />
          )}

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              flex: 1,
              padding: '4px 8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <SolidCheckbox
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={() => handleCheckboxChange(node)}
            />
            <span style={{
              fontSize: '15px',
              color: '#1f2937',
              fontWeight: '500',
              letterSpacing: '-0.01em'
            }}>
              {node.label}
            </span>
          </label>
        </div>

        {hasChildren && isExpanded && (
          <div style={{ position: 'relative' }}>
            {node.children.map((child, i) => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                isLast={i === node.children.length - 1}
                parentChain={level === 0 ? [] : [...parentChain, !isLast]}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Input
        type="text"
        value={search_value}
        placeholder="Cari kategori... Tekan Enter"
        onChange={e => {
          set_search_value(e.target.value)
        }}
        onKeyDown={(e) => { if (e.key === "Enter") on_filter(); }}
      />

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)'
      }}>
        {treeData.current?.map((node, i) => (
          <TreeNode key={node.id} node={node} isLast={i === treeData.current?.length - 1} />
        ))}
      </div>
    </div>
  );
};

export default CheckboxTree;